import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Col, Row } from 'react-bootstrap';
import snakecaseKeys from 'snakecase-keys';

import { SimpleCenteredModal } from '../..';
import { sendAlert } from '../../../actions/utils';
import { createAdminLeadQuotationRequest } from '../../../requests/admin/leadQuotation';
import LeadSuccessCloseModal from '../../../screens/Lead/LeadSuccessCloseModal';
import { scrollToTop } from '../../../services/utils';
import sendDocumentEmail from '../../Shared/SendDocumentEmail';
import { leadQuotationProps } from '../../Shared/SendEmailModalProps';
import { IconBtn } from '../../Utils/Button';
import { InfoCard } from '../../Utils/Cards';
import basicLeadQuotation from '../basicLeadQuotation';
import CloseQuotationModal from '../CloseQuotationModal';
import LeadQuotationForm from '../LeadQuotationForm';
import SelectQuotation from '../SelectQuotation';

const InfoCardLeadQuotation = ({ fromAdmin, canCloseQuotation, lead, leadQuotationMessage, modelUrl, setMoreData }) => {
  const { leadQuotations, selectedLeadQuotation } = lead;

  const [modalBody, setModalBody] = useState('');
  const [modalShow, setModalShow] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedQuotation, setSelectedQuotation] = useState(selectedLeadQuotation?.id || '');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const dispatch = useDispatch();

  const { SendEmailModal, openEmailModal } = sendDocumentEmail({
    fromAdmin,
    executiveModel: { id: lead.id, name: 'Lead' }
  });

  const hasQuotations = leadQuotations.length > 0;
  const isDisabled = !selectedQuotation || !selectedFile;

  const handleModalSuccessClose = () => {
    setMoreData(m => !m);
  };

  const handleModalClose = () => {
    setModalShow(false);
    setModalBody('');
  };

  const handleCreateRequest = values => {
    const { quotation } = values.leadQuotation;
    const sendParams = snakecaseKeys(values);
    sendParams.lead_quotation.quotation = quotation;
    createAdminLeadQuotationRequest(lead.id, {
      dispatch,
      params: sendParams,
      formData: true,
      successCallback: () => {
        handleModalClose();
        dispatch(sendAlert({ kind: 'success', message: 'Cotización cargada con éxito' }));
        setMoreData(m => !m);
      }
    });
  };

  const handleCreateLeadQuotation = () => {
    setModalShow(true);
    setModalBody(
      <LeadQuotationForm
        action="new"
        leadQuotation={basicLeadQuotation}
        show={modalShow}
        formRequest={handleCreateRequest}
        onHide={handleModalClose}
        insuranceCategory={lead.insuranceCategory}
      />
    );
  };

  const handleCloseLeadQuotation = isSuccess => {
    setModalShow(true);
    setModalBody(
      <CloseQuotationModal
        leadId={lead.id}
        modelUrl={modelUrl}
        fromAdmin={fromAdmin}
        isSuccess={isSuccess}
        selectedFile={selectedFile}
        selectedQuotation={selectedQuotation}
        handleModalClose={handleModalClose}
        setShowSuccessModal={setShowSuccessModal}
      />
    );
  };

  const purgeSelectedFile = () => setSelectedFile(null);

  useEffect(scrollToTop, []);
  useEffect(purgeSelectedFile, [selectedQuotation]);

  return (
    <>
      {showSuccessModal && <LeadSuccessCloseModal showModal={showSuccessModal} handleClose={handleModalSuccessClose} />}

      <InfoCard
        title={fromAdmin ? 'Cotizaciones adjuntas' : 'Cotizaciones disponibles'}
        className="bg-light-gray bg-children-inherit mb-5"
        iconBtnComponent={
          canCloseQuotation && (
            <div className="d-flex">
              <Button className="mr-4" onClick={openEmailModal}>
                Enviar
              </Button>

              {fromAdmin && (
                <IconBtn
                  icon="software-upload"
                  variant="primary-dark"
                  className="no-shadow"
                  onClick={handleCreateLeadQuotation}
                >
                  Adjuntar Cotización
                </IconBtn>
              )}
            </div>
          )
        }
      >
        <CardMessage hasQuotations={hasQuotations} leadQuotationMessage={leadQuotationMessage} />

        {hasQuotations && (
          <CardQuotationBody
            lead={lead}
            modelUrl={modelUrl}
            fromAdmin={fromAdmin}
            handleSubmitButton={handleCloseLeadQuotation}
            selectedQuotation={selectedQuotation}
            setSelectedQuotation={setSelectedQuotation}
            setSelectedFile={setSelectedFile}
            setMoreData={setMoreData}
            isDisabled={isDisabled}
            canCloseQuotation={canCloseQuotation}
          />
        )}

        <SimpleCenteredModal show={modalShow} body={modalBody} onHide={handleModalClose} />

        <SendEmailModal {...leadQuotationProps({ lead })} />
      </InfoCard>
    </>
  );
};

const CardMessage = ({ hasQuotations, leadQuotationMessage }) =>
  hasQuotations ? (
    <div className="mt-4">
      {leadQuotationMessage.full.map((message, index) => (
        <p key={`card-message-${index.toString()}`} className="mb-4">
          {message}
        </p>
      ))}
    </div>
  ) : (
    <p className="section-title text-center d-block mt-5">{leadQuotationMessage.empty}</p>
  );

const CardQuotationBody = ({ fromAdmin, handleSubmitButton, isDisabled, lead, modelUrl, ...props }) => {
  const { canCloseQuotation, setMoreData, setSelectedFile, selectedQuotation, setSelectedQuotation } = props;

  return (
    <>
      {lead.leadQuotations.map(item => (
        <SelectQuotation
          key={item.id}
          lead={lead}
          fromAdmin={fromAdmin}
          currentLeadQuotation={item}
          setSelectedQuotation={setSelectedQuotation}
          isSelected={item.id === selectedQuotation}
          setSelectedFile={setSelectedFile}
          setMoreData={setMoreData}
          canCloseQuotation={canCloseQuotation}
          indexPath={modelUrl}
        />
      ))}

      {canCloseQuotation && (
        <Row className="d-flex flex-row-reverse mt-5">
          <Col md={4} lg={3} className="mt-4">
            <Button
              block
              type="submit"
              variant="submit"
              className="no-shadow"
              onClick={() => handleSubmitButton(true)}
              disabled={isDisabled}
            >
              Cierre con éxito
            </Button>
          </Col>
          <Col md={4} lg={3} className="mt-4">
            <Button block variant="strong-danger" className="no-shadow" onClick={() => handleSubmitButton(false)}>
              Cierre sin éxito
            </Button>
          </Col>
        </Row>
      )}
    </>
  );
};

export default InfoCardLeadQuotation;
